import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createTeam } from "@/helpers/apis";
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Textarea,
  TooltipContent,
  TooltipCore,
  TooltipTrigger,
  toast,
} from "@altimateai/lego";
import { useState } from "react";
import { LoadingButton, Stack } from '@altimateai/lego'
import { AddIcon } from "@/assets/icons";

const formSchema = z.object({
  name: z.string().min(1, "Team name is required"),
  description: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

const AddNewTeam = () => {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
    },
  });

  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: (data: FormValues) => createTeam(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["UserGroups"] });
      toast({ description: "Team created successfully" });
      setOpen(false);
      form.reset();
    },
    onError: (error: Error) => {
      toast({
        description: `Failed to create team: ${error.message || error}`,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: FormValues) => {
    mutate(data);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <TooltipCore>
        <DialogTrigger asChild>
          <TooltipTrigger asChild>
            <Button variant="outline" size="sm" aria-label="Add New Team">
              <AddIcon className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
        </DialogTrigger>
        <TooltipContent>Add New Team</TooltipContent>
      </TooltipCore>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Create New Team</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" role="form">
            <Stack direction="column" gap="xl">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Team Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter team name" {...field} className="!al-m-0" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description (Optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Enter team description"
                        className="resize-none !al-m-0"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Stack justify="end">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <LoadingButton type="submit" loading={isLoading}>
                  {isLoading ? "Creating..." : "Create Team"}
                </LoadingButton>
              </Stack>
            </Stack>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AddNewTeam;
